import React, { useState, useEffect } from 'react'
import { Form, useFormikContext } from 'formik'
import { useQuery } from 'react-query'
import { useTranslate } from 'react-admin'
import { Divider, Switch, Typography } from '@material-ui/core'
import PropTypes from 'prop-types'
import BarChart from '@material-ui/icons/BarChart'
import ScheduleIcon from '@material-ui/icons/Schedule'
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined'

import '../../../../../utils/constants/CSS/eventForms.css'
import {
  isFormFieldDisabled,
  isBroadcastEnded,
  isStreamTerminal,
} from '../../../../../utils/helpers/isFieldDisabled'
import FormContainer from '../../../../layout/FormContainer/FormContainer'
import MyDateField from '../../../fields/MyDateField'
import MyTimeField from '../../../fields/MyTimeField'
import CourtSelectField from '../../../fields/CourtSelectField/CourtSelectField'
import MyMultilineTextField from '../../../fields/MyMultilineTextField'
import IsUserAddonsAuthorized from '../../../../IsUserAddonsAuthorized/IsUserAddonsAuthorized'
import IsOrgAddonsAuthorized from '../../../../IsOrgAddonsAuthorized/IsOrgAddonsAuthorized'
import EventFormLivestreamFields from '../../../../EventFormLivestreamFields/EventFormLivestreamFields'
import GetFormDirtyValue from '../../../../getFormDirtyValue/getFormDirtyValue'
import AutoChangeFakeEvent from '../../../AutoChangeFakeEvent'
import onEventFieldChange from '../../../../../utils/helpers/eventHelpers/onEventFieldChange'
import { USER_ADDONS } from '../../../../../utils/constants/userAddons'
import EventFormCourtLogos from '../../../../EventFormCourtLogos/EventFormCourtLogos'
import EventFormSharedRevenueToggle from '../../../../EventFormSharedRevenueToggle/EventFormSharedRevenueToggle'
import GAME_EVENT_FORM_BROADCAST_FIELDS from '../../../../../utils/constants/gameEventFormBroadcastFields'
import courtType from '../../../../../types/courtType'
import mutationType from '../../../../../types/mutationType'
import teamType from '../../../../../types/teamType'
import eventVariantType from '../../../../../types/events/eventVariantType'
import eventType from '../../../../../types/events/eventType'
import eventRawType from '../../../../../types/events/eventRawType'
import isCourtLivestreamEnabled from '../../../../../utils/helpers/isCourtLivestreamEnabled'
import AddCourtModal from '../../../../ui/AddCourtModal/AddCourtModal'
import AddButton from '../../../../ui/AddButton/AddButton'
import GameSubmitConfirmationModal from '../../../../GameSubmitConfirmationModal/GameSubmitConfirmationModal'
import ids from '../../../../../utils/constants/introIds'
import EventTeamsFields from '../EventTeamsFields/EventTeamsFields'
import MyCourtIcon from '../../../../../assets/icons/MyCourtIcon'
import ORGANISATION_TYPES from '../../../../../utils/constants/OrganisationTypes'
import MyTextField from '../../../fields/MyTextField'
import ConnectedGameField from './ConnectedGameField'
import useGamePairingCandidates, { getClubTeamId } from './useGamePairingCandidates'
import isOrgAddonsValid from '../../../../../utils/helpers/isOrgAddonsValid'
import isUserAddonsValid from '../../../../../utils/helpers/isUserAddonsValid'
import ORGANISATION_SPORTS from '../../../../../utils/constants/organisationSports'
import LiteModeBadge from '../../../../LiteModeBadge/LiteModeBadge'
import { STREAM_FEATURES, supports } from '../../../../../utils/constants/streamTypeCapabilities'
import useGetLocalStorage from '../../../../../hooks/useGetLocalStorage'
import localStorageIds from '../../../../../utils/constants/localStorageIds'
import getStudioOperators from '../../../../../Apis/studio/getStudioOperators'
import getStatisticians from '../../../../../Apis/stats/getStatisticians'
import getVideoOperators from '../../../../../Apis/videoOperators/getVideoOperators'
import GameDrawerTabs from '../../../../GameFormLayout/GameDrawerTabs'
import SectionFieldset from '../../../../GameFormLayout/SectionFieldset'
import PersonSelectCard from '../../../../GameFormLayout/PersonSelectCard'
import ThirdPartyStreamField from '../../../../GameFormLayout/ThirdPartyStreamField'
import SubmitErrorNavigator from '../../../../GameFormLayout/SubmitErrorNavigator'
import GameResultSection from '../../../../GameFormLayout/GameResultSection/GameResultSection'

// Fields the validators can flag, in top-to-bottom visual order, mapped to their tab.
const FIELD_ORDER = [
  { field: 'fromDate', tab: 'details' },
  { field: 'startTime', tab: 'details' },
  { field: 'endTime', tab: 'details' },
  { field: 'courtId', tab: 'details' },
  { field: 'homeTeam', tab: 'details' },
  { field: 'awayTeam', tab: 'details' },
  { field: 'streamDestination', tab: 'streaming' },
  { field: 'statisticianIds', tab: 'stats' },
]

// When `selectedIds` (an array) is passed, `selected` is the array of matching options
// (multi-select). Otherwise `selectedId` (a scalar) yields a single option or null.
const usePersonOptions = (queryKey, fetcher, { enabled, selectedId, selectedIds }) => {
  const { data } = useQuery(queryKey, fetcher, { enabled })
  const options =
    data?.map((p) => ({
      id: p.id,
      name: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(),
    })) ?? []
  const selected = selectedIds
    ? options.filter((o) => selectedIds.includes(o.id))
    : options.find((o) => o.id === selectedId) ?? null
  return { options, selected }
}

const GameEventFormFields = ({
  event,
  state,
  orgType,
  courts,
  teams,
  selectedEventType,
  clickedEventId,
  eventsRaw,
  setEvents,
  setFormDirty,
  allowOrgYoutube,
  broadcastId,
  setMediaIsUploading,
  isUploadingModalOpen,
  setIsUploadingModalOpen,
  statsMode,
  onGameSubmit,
}) => {
  const { values, setFieldValue, errors, submitCount } = useFormikContext()
  const translate = useTranslate()
  const [isCourtModalOpen, setIsCourtModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('details')
  const hasTabError = (tab) =>
    submitCount > 0 && FIELD_ORDER.some((f) => f.tab === tab && errors[f.field])
  // Streaming/Stats tabs are only offered when the org actually holds the matching addon
  // (clubs, for instance, can never have the Stats addon), mirroring the addon gate on each
  // tab's content so we never surface a tab that would render empty.
  const showStreamingTab = isUserAddonsValid([USER_ADDONS.LIVESTREAM])
  const showStatsTab = isOrgAddonsValid([USER_ADDONS.STATS])
  // Streaming is only meaningful on a livestream-capable court, so the tab stays disabled until
  // such a court is picked (in Details); a tooltip explains why.
  const streamingTabDisabled = !isCourtLivestreamEnabled(courts, values?.courtId)
  const tabs = [
    { key: 'details', label: translate('ra.gameDrawer.tabs.details'), error: hasTabError('details') },
    ...(showStreamingTab
      ? [
          {
            key: 'streaming',
            label: translate('ra.gameDrawer.tabs.streaming'),
            showDot: Boolean(
              values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST] ||
                values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD],
            ),
            error: hasTabError('streaming'),
            disabled: streamingTabDisabled,
            disabledHint: translate('ra.gameDrawer.noStreamCourt'),
          },
        ]
      : []),
    ...(showStatsTab
      ? [
          {
            key: 'stats',
            label: translate('ra.gameDrawer.tabs.stats'),
            showDot: Boolean(values.hasStats),
            error: hasTabError('stats'),
          },
        ]
      : []),
  ]
  // If the court is changed to one without streaming, don't leave the user stranded on the
  // now-disabled streaming tab.
  useEffect(() => {
    if (activeTab === 'streaming' && streamingTabDisabled) setActiveTab('details')
  }, [activeTab, streamingTabDisabled])

  const { onFromDateFieldChange, onStartTimeFieldChange, onEndTimeFieldChange } = onEventFieldChange

  const isClub = orgType === ORGANISATION_TYPES.ORG_CLUB
  const gamePairingOptions = useGamePairingCandidates({
    clubTeamId: isClub ? getClubTeamId(values.teams) : null,
    existingPairedGame: event?.pairedGame,
    enabled: isClub,
  })

  const localStorageValue = useGetLocalStorage()
  const { [localStorageIds.SELECTED_ORG]: selectedOrg } = localStorageValue || {}
  // Studio is a Pro tier feature, not an add-on, so the tier and a broadcast destination are the
  // whole requirement — the operator pool is everyone in the org.
  const showOperatorPicker =
    supports(values[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE], STREAM_FEATURES.STUDIO) &&
    values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST]

  // Court-logo overlays apply to the produced tiers (the submit parser gates the court-logo
  // fields on the same condition), so the ADS section is shown only for those.
  const isLivestreamTypeFull =
    (values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST] ||
      values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD]) &&
    supports(values[GAME_EVENT_FORM_BROADCAST_FIELDS.TYPE], STREAM_FEATURES.COURT_LOGOS)

  const { options: studioOperatorOptions, selected: selectedStudioOperator } = usePersonOptions(
    ['getStudioOperators', selectedOrg?.id],
    () => getStudioOperators(selectedOrg.id),
    {
      enabled: Boolean(showOperatorPicker && selectedOrg?.id),
      selectedId: values.studioOperatorId,
    },
  )

  const hasStatsAddon = selectedOrg?.addons?.includes(USER_ADDONS.STATS)
  const { options: statisticianOptions, selected: selectedStatistician } = usePersonOptions(
    ['getStatisticians', selectedOrg?.id],
    () => getStatisticians(selectedOrg.id),
    {
      enabled: Boolean(hasStatsAddon && values.hasStats && selectedOrg?.id),
      selectedIds: values.statisticianIds,
    },
  )

  // Video operators download the overlay links for a game streamed by a third-party provider.
  // They don't need the third-party link: the CMS sync can clear it, and the assignment stays.
  const { options: videoOperatorOptions, selected: selectedVideoOperators } = usePersonOptions(
    ['getVideoOperators', selectedOrg?.id],
    () => getVideoOperators(selectedOrg.id),
    {
      enabled: Boolean(selectedOrg?.id),
      selectedIds: values.videoOperatorIds ?? [],
    },
  )

  const selectedCourt = courts?.find((item) => item.id === values.courtId)

  const studioOperatorField = showOperatorPicker && (
    <PersonSelectCard
      fieldName='studioOperatorId'
      label={translate('ra.gameDrawer.studioOperator')}
      options={studioOperatorOptions}
      selected={selectedStudioOperator}
      onChange={(id) => setFieldValue('studioOperatorId', id)}
      disabled={isStreamTerminal(state)}
    />
  )

  useEffect(() => {
    if (values.courtId && !event?.broadcast?.gameStateTrackingType) {
      const courtAutoEnabled =
        selectedCourt?.livestreamSettings?.gameStateTrackingAutoEnabled || false
      setFieldValue(GAME_EVENT_FORM_BROADCAST_FIELDS.GAME_STATE_TRACKING_AUTO, courtAutoEnabled)
    }
  }, [values.courtId])

  // noValidate: the tabs keep hidden panels mounted (display:none), so native HTML5 constraint
  // validation aborts submit when a `required` field in an inactive tab isn't focusable
  // ("invalid form control is not focusable"). Disabling it lets Formik's JS validation run and
  // SubmitErrorNavigator move the user to the offending tab.
  return (
    <Form id={ids.createEvent.form} noValidate>
      <GameDrawerTabs active={activeTab} onChange={setActiveTab} tabs={tabs} />

      <div style={{ display: activeTab === 'details' ? 'block' : 'none' }}>
        <FormContainer>
          <div className='div-flex'>
            <ScheduleIcon className='icon' style={{ fill: 'grey' }} />
            <div id={ids.createEvent.time} style={{ display: 'flex', gap: 12, flex: 1 }}>
              <div style={{ flex: 1 }}>
                <MyDateField
                  id={ids.createEvent.date}
                  name='fromDate'
                  label={translate('ra.formFields.date')}
                  required
                  disabled={isFormFieldDisabled(state)}
                  onChange={onFromDateFieldChange}
                />
              </div>
              <div style={{ flex: 1 }}>
                <MyTimeField
                  name='startTime'
                  label={translate('ra.formFields.startTime')}
                  required
                  disabled={isFormFieldDisabled(state)}
                  onChange={onStartTimeFieldChange}
                />
              </div>
              <div style={{ flex: 1 }}>
                <MyTimeField
                  name='endTime'
                  label={translate('ra.formFields.endTime')}
                  required
                  disabled={isFormFieldDisabled(state, 'endTime')}
                  onChange={onEndTimeFieldChange}
                />
              </div>
            </div>
          </div>

          <div id={ids.createEvent.court} className='div-flex'>
            <MyCourtIcon />
            <CourtSelectField
              name='courtId'
              label={translate('ra.formFields.courtName')}
              courts={courts}
              emptyText={translate('ra.text.emptyCourts')}
              fullWidth
              required={Boolean(
                values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST] ||
                  values[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_RECORD],
              )}
              disabled={isFormFieldDisabled(state)}
              addOptionsComponent={
                <AddButton
                  onClick={() => setIsCourtModalOpen(true)}
                  label={translate('ra.action.add_court')}
                  noHoverEffect={courts?.length}
                  style={{
                    paddingLeft: !courts?.length ? 8 : 4,
                    marginRight: !courts?.length ? 10 : 0,
                  }}
                />
              }
            />
            <AddCourtModal
              isCourtModalOpen={isCourtModalOpen}
              setIsCourtModalOpen={setIsCourtModalOpen}
              setValue={(newCourtValue) => setFieldValue('courtId', newCourtValue)}
            />
          </div>

          <div id={ids.createEvent.team}>
            <Divider style={{ margin: '4px 0 16px' }} />
            <span
              style={{
                display: 'block',
                marginBottom: 12,
                textTransform: 'uppercase',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '.08em',
                color: '#9A9A9A',
              }}
            >
              {translate('ra.gameDrawer.sections.matchup')}
            </span>
            <EventTeamsFields
              state={state}
              orgType={orgType}
              teams={teams}
              preFilledAway={true}
            />
          </div>

          <MyMultilineTextField
            name='notes'
            label={translate('ra.formFields.notes')}
            multiline
            minRows={5}
            fullWidth
            disabled={isFormFieldDisabled(state)}
          />

          <ConnectedGameField
            renderValue={gamePairingOptions.length > 0}
            options={gamePairingOptions}
            state={state}
          />

          <ThirdPartyStreamField state={state} hasContent={Boolean(values.videoOperatorIds?.length)}>
            <PersonSelectCard
              fieldName='videoOperatorIds'
              label={translate('ra.gameDrawer.videoOperator')}
              multiple
              options={videoOperatorOptions}
              selected={selectedVideoOperators}
              onChange={(ids) => setFieldValue('videoOperatorIds', ids)}
              disabled={isBroadcastEnded(state)}
            />
          </ThirdPartyStreamField>
        </FormContainer>
      </div>

      <div style={{ display: activeTab === 'streaming' ? 'block' : 'none' }}>
        <FormContainer>
          <IsUserAddonsAuthorized acceptedAddons={[USER_ADDONS.LIVESTREAM]}>
            {isCourtLivestreamEnabled(courts, values?.courtId) ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <EventFormLivestreamFields
                  state={state}
                  gameStateTrackingAutoEnabled={
                    selectedCourt?.livestreamSettings?.gameStateTrackingAutoEnabled || false
                  }
                  allowOrgYoutube={allowOrgYoutube}
                  broadcast={event?.broadcast}
                  broadcastId={broadcastId ?? null}
                  gameId={clickedEventId ?? null}
                  selectedCourt={selectedCourt}
                  selectedEventType={selectedEventType}
                  studioOperatorField={studioOperatorField}
                />
                {isLivestreamTypeFull && (
                  <SectionFieldset
                    label={translate('ra.gameDrawer.sections.ads')}
                    style={{ paddingBottom: 18 }}
                  >
                    <EventFormSharedRevenueToggle state={state} />
                    <EventFormCourtLogos
                      state={state}
                      setMediaIsUploading={setMediaIsUploading}
                      selectedEventType={selectedEventType}
                      sport={ORGANISATION_SPORTS.BASKETBALL}
                    />
                  </SectionFieldset>
                )}
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  backgroundColor: '#F7F6F4',
                  borderRadius: 10,
                  padding: '14px 16px',
                }}
              >
                <VideoLibraryOutlinedIcon style={{ color: '#A0A0A0' }} />
                <span style={{ fontSize: 13, color: '#6B6B6B' }}>
                  {translate('ra.gameDrawer.noStreamCourt')}
                </span>
              </div>
            )}
          </IsUserAddonsAuthorized>
        </FormContainer>
      </div>

      <div style={{ display: activeTab === 'stats' ? 'block' : 'none' }}>
        <FormContainer>
          <IsOrgAddonsAuthorized
            acceptedAddons={[USER_ADDONS.STATS]}
            renderValue={isOrgAddonsValid([USER_ADDONS.STATS])}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  backgroundColor: '#F7F6F4',
                  borderRadius: 12,
                  padding: '14px 16px',
                }}
              >
                <BarChart style={{ color: '#7A7A7A' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Typography style={{ fontSize: 15, fontWeight: 600, color: '#3C3C3C' }}>
                      {translate('ra.gameDrawer.stats.trackStats')}
                    </Typography>
                    {values.hasStats && <LiteModeBadge statsMode={statsMode} />}
                  </div>
                  <Typography style={{ fontSize: 12, color: '#9A9A9A' }}>
                    {translate('ra.gameDrawer.stats.trackStatsDesc')}
                  </Typography>
                </div>
                <Switch
                  name='hasStats'
                  color='primary'
                  checked={values.hasStats}
                  onChange={() => setFieldValue('hasStats', !values.hasStats)}
                />
              </div>

              <PersonSelectCard
                fieldName='statisticianIds'
                label={translate('ra.gameDrawer.statistician')}
                avatarColor='#7BA23F'
                multiple
                options={statisticianOptions}
                selected={selectedStatistician}
                onChange={(ids) => setFieldValue('statisticianIds', ids)}
                disabled={!values.hasStats}
                changeLabel={translate('ra.buttons.change')}
              />

              <MyTextField
                name='externalId'
                label='Game External Id'
                disabled={isBroadcastEnded(state) || !values.hasStats}
              />

              {event?.id && (
                <GameResultSection
                  orgId={selectedOrg?.id}
                  gameId={event.id}
                  eventTeams={event.eventTeams}
                  teamIds={event.teams?.map((team) => team.id) ?? []}
                  onResultSaved={() => {
                    if (!values.hasStats) setFieldValue('hasStats', true)
                  }}
                />
              )}
            </div>
          </IsOrgAddonsAuthorized>
        </FormContainer>
      </div>

      <SubmitErrorNavigator
        fieldOrder={FIELD_ORDER}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      <GetFormDirtyValue setFormDirty={setFormDirty} />
      <AutoChangeFakeEvent
        clickedEventId={clickedEventId}
        selectedEventType={selectedEventType}
        eventsRaw={eventsRaw}
        setEvents={setEvents}
      />
      <GameSubmitConfirmationModal
        isOpen={isUploadingModalOpen}
        onCancelClick={() => setIsUploadingModalOpen(false)}
        onCreateClick={() => {
          setIsUploadingModalOpen(false)
          onGameSubmit(values)
        }}
      />
    </Form>
  )
}

GameEventFormFields.propTypes = {
  event: eventType,
  state: PropTypes.string,
  orgType: PropTypes.string,
  courts: PropTypes.arrayOf(courtType),
  teams: PropTypes.arrayOf(teamType),
  selectedEventType: eventVariantType,
  clickedEventId: PropTypes.string,
  eventsRaw: PropTypes.arrayOf(eventRawType),
  setEvents: PropTypes.func,
  setFormDirty: PropTypes.func,
  allowOrgYoutube: PropTypes.bool,
  broadcastId: PropTypes.string,
  mediaIsUploading: PropTypes.bool,
  setMediaIsUploading: PropTypes.func,
  isUploadingModalOpen: PropTypes.bool,
  setIsUploadingModalOpen: PropTypes.func,
  onGameSubmit: PropTypes.func,
  createEvent: mutationType,
  statsMode: PropTypes.string,
  updateEvent: mutationType,
}

export default GameEventFormFields
